<script setup>
/* global axios */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AModal from './AModal.vue';
import { DAYS, ICON } from './constants';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;

const practitioners = ref([]);
const services = ref([]);
const show = ref(false);
const form = ref({});
const hours = ref({});
const lunch = ref(false);
const sel = ref([]);

const load = async () => {
  practitioners.value =
    (await axios.get(`${base}/practitioners`)).data.data || [];
  services.value =
    (await axios.get(`${base}/appointment_services`)).data.data || [];
};
const open = (p = null) => {
  if (p) {
    form.value = { ...p };
    sel.value = [...(p.service_ids || [])];
    lunch.value = !!(p.lunch_start && p.lunch_end);
    hours.value = {};
    (p.working_hours || []).forEach(w => {
      hours.value[w.day] = {
        enabled: true,
        startTime: w.startTime,
        endTime: w.endTime,
      };
    });
  } else {
    form.value = {
      name: '',
      specialty: '',
      registration_no: '',
      email: '',
      phone: '',
      color: '#6366F1',
      buffer_minutes: 0,
      lunch_start: '12:00',
      lunch_end: '13:00',
      is_active: true,
    };
    sel.value = [];
    lunch.value = false;
    hours.value = {};
  }
  DAYS.forEach(([, d]) => {
    if (!hours.value[d])
      hours.value[d] = { enabled: false, startTime: '08:00', endTime: '18:00' };
  });
  show.value = true;
};
const save = async () => {
  if (!form.value.name?.trim()) return;
  const wh = DAYS.filter(([, d]) => hours.value[d]?.enabled).map(([, d]) => ({
    day: d,
    startTime: hours.value[d].startTime,
    endTime: hours.value[d].endTime,
  }));
  const payload = {
    ...form.value,
    working_hours: JSON.stringify(wh),
    service_ids: sel.value,
  };
  if (!lunch.value) {
    payload.lunch_start = null;
    payload.lunch_end = null;
  }
  if (form.value.id)
    await axios.patch(`${base}/practitioners/${form.value.id}`, payload);
  else await axios.post(`${base}/practitioners`, payload);
  show.value = false;
  await load();
};
const remove = async p => {
  if (
    !window.confirm(
      t('ATENDIMENTOS.practitioners.deleteConfirm', { name: p.name })
    )
  )
    return;
  await axios.delete(`${base}/practitioners/${p.id}`);
  await load();
};
const toggleSvc = id => {
  const i = sel.value.indexOf(id);
  if (i >= 0) sel.value.splice(i, 1);
  else sel.value.push(id);
};
const svcName = id => services.value.find(s => s.id === id)?.name;

onMounted(load);
</script>

<template>
  <div class="p-6">
    <div class="flex justify-end mb-4">
      <button
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
        @click="open()"
      >
        {{ t('ATENDIMENTOS.practitioners.newPractitioner') }}
      </button>
    </div>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="p in practitioners"
        :key="p.id"
        class="p-4 bg-n-solid-2 border rounded-xl border-n-weak"
      >
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-3">
            <span
              class="flex items-center justify-center w-10 h-10 text-sm font-semibold text-white rounded-full"
              :style="{ background: p.color }"
              >{{ (p.name || '?')[0] }}</span
            >
            <div>
              <div class="font-semibold text-n-slate-12">{{ p.name }}</div>
              <div class="text-sm text-n-slate-10">
                {{ p.specialty || '—' }}
              </div>
            </div>
          </div>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="open(p)"
          >
            {{ t('ATENDIMENTOS.practitioners.edit') }}
          </button>
        </div>
        <div
          v-if="p.lunch_start"
          class="inline-flex items-center gap-1 mt-2 px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800"
        >
          <svg
            class="w-3 h-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" :d="ICON.clock[0]" />
          </svg>
          {{ p.lunch_start }}–{{ p.lunch_end }}
        </div>
        <div class="flex flex-wrap gap-1.5 mt-3">
          <span
            v-for="sid in p.service_ids || []"
            :key="sid"
            class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-1 text-n-slate-11"
            >{{ svcName(sid) }}</span
          >
        </div>
        <button
          class="mt-3 text-xs text-n-ruby-11 hover:underline"
          @click="remove(p)"
        >
          {{ t('ATENDIMENTOS.patients.delete') }}
        </button>
      </div>
      <p v-if="!practitioners.length" class="text-n-slate-10">
        {{ t('ATENDIMENTOS.practitioners.empty') }}
      </p>
    </div>

    <AModal
      v-if="show"
      :title="
        form.id
          ? t('ATENDIMENTOS.practitioners.modal.editTitle')
          : t('ATENDIMENTOS.practitioners.modal.newTitle')
      "
      @close="show = false"
    >
      <div class="flex gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.namefield')
          }}</label
          ><input
            v-model="form.name"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.color')
          }}</label
          ><input
            v-model="form.color"
            type="color"
            class="w-12 h-9 p-0.5 border rounded border-n-weak bg-n-background"
          />
        </div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.specialty')
          }}</label
          ><input
            v-model="form.specialty"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.registrationNo')
          }}</label
          ><input
            v-model="form.registration_no"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.email')
          }}</label
          ><input
            v-model="form.email"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.practitioners.modal.phone')
          }}</label
          ><input
            v-model="form.phone"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
      </div>
      <div>
        <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.availableDays')
        }}</label>
        <div class="space-y-1.5">
          <div
            v-for="[lbl, d] in DAYS"
            :key="d"
            class="flex items-center gap-3"
          >
            <button
              type="button"
              class="w-16 shrink-0 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors"
              :class="
                hours[d].enabled
                  ? 'bg-n-brand text-white border-n-brand'
                  : 'bg-n-background text-n-slate-11 border-n-weak hover:border-n-slate-6'
              "
              @click="hours[d].enabled = !hours[d].enabled"
            >
              {{ t(lbl) }}
            </button>
            <div
              v-if="hours[d]?.enabled"
              class="flex items-center gap-2 flex-1 min-w-0"
            >
              <input
                v-model="hours[d].startTime"
                type="time"
                class="reset-base flex-1 min-w-0 h-8 px-2 text-xs rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12"
              /><span class="text-xs text-n-slate-10 shrink-0">{{
                t('ATENDIMENTOS.practitioners.modal.to')
              }}</span
              ><input
                v-model="hours[d].endTime"
                type="time"
                class="reset-base flex-1 min-w-0 h-8 px-2 text-xs rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <span v-else class="text-xs text-n-slate-9">{{
              t('ATENDIMENTOS.practitioners.modal.unavailable')
            }}</span>
          </div>
        </div>
      </div>
      <div>
        <label class="flex items-center gap-2 text-sm"
          ><input v-model="lunch" type="checkbox" />
          {{ t('ATENDIMENTOS.practitioners.modal.lunchLabel') }}</label
        >
        <div v-if="lunch" class="flex items-center gap-2 mt-1">
          <input
            v-model="form.lunch_start"
            type="time"
            class="w-full px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          /><span class="text-n-slate-10">{{
            t('ATENDIMENTOS.practitioners.modal.lunchTo')
          }}</span
          ><input
            v-model="form.lunch_end"
            type="time"
            class="w-full px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <p v-if="lunch" class="mt-1 text-[0.6875rem] text-n-slate-10">
          {{ t('ATENDIMENTOS.practitioners.modal.lunchNote') }}
        </p>
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.buffer')
        }}</label
        ><input
          v-model="form.buffer_minutes"
          type="number"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.practitioners.modal.enabledServices')
        }}</label>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="s in services"
            :key="s.id"
            class="px-2 py-1 text-xs rounded-full border"
            :class="
              sel.includes(s.id)
                ? 'bg-n-brand text-white border-n-brand'
                : 'bg-n-background text-n-slate-11 border-n-weak'
            "
            @click="toggleSvc(s.id)"
          >
            {{ s.name }}
          </button>
        </div>
      </div>
      <template #footer>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="show = false"
        >
          {{ t('ATENDIMENTOS.practitioners.modal.cancel') }}</button
        ><button
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          @click="save"
        >
          {{ t('ATENDIMENTOS.practitioners.modal.save') }}
        </button>
      </template>
    </AModal>
  </div>
</template>
